import React, { useMemo, useContext } from 'react';

import * as Popper from '@popperjs/core';
if (typeof window !== 'undefined') window.Popper = Popper;

import { pagePublicGet, pagePreviewGet } from '@api/cms';

import { PageHeader } from '@utils/page-header';
import { isRedirectCode } from '@utils/request';

import { useSoftRedirect } from '@hooks/use-soft-redirect';
import { useTypeformParamsAppender } from '@hooks/use-typeform-params-appender';
import { useGAAutoBinding } from '@hooks/use-ga-auto-binding';

import { auth, Component, getTokenFromRequest } from '@modules/base/auth';
import {
  GoogleOptimizeWrapper,
  GoogleOptimizeContext,
} from '@modules/providers/google-optimize';
import { Root } from '@modules/base';
import { ErrorPage } from '@modules/pages/error';

import { Page as PageType } from '@typings/page';

import { usePageScripts, PARSE_SCRIPT_REGEX } from './page.hooks';

import classes from './page.module.scss';

type Props = {
  pages: PageType[];
};

const PageComponent: Component<Props> = ({ pages }) => {
  const { optimizeVariants } = useContext(GoogleOptimizeContext);

  const { page, isHtmlAb } = useMemo(() => {
    let isHtmlAb = false;

    if (!pages.length) return { page: undefined, isAb: isHtmlAb };

    if (pages.length === 1) return { page: pages[0], isAb: isHtmlAb };

    const selectedPage = pages.find(({ optimizeVariant }) => {
      // Page has no optimize, prefer it
      if (!optimizeVariant) return true;
      isHtmlAb = true;

      const optimizeMatch = /(.*):(\d)/.exec(optimizeVariant);

      // Page has invalid optimize, prefer it
      if (!optimizeMatch) return true;

      const [, experiment, variant] = optimizeMatch;

      // No optimize, show A variant (probably server or blocking)
      if (
        !Object.keys(optimizeVariants).length ||
        !optimizeVariants[experiment]
      ) {
        return variant === '0' || !variant;
      }

      if (typeof optimizeVariants[experiment] !== 'undefined') {
        return optimizeVariants[experiment] === variant;
      }

      // Fall thru, optimize variant doesn't match
      return false;
    });

    return {
      isHtmlAb,
      page: selectedPage,
    };
  }, [pages, optimizeVariants]);

  useTypeformParamsAppender([page]);
  useGAAutoBinding([page]);

  const shouldAB = useMemo(
    () => typeof page !== 'undefined' && (!page.gjs || !page.gjs['gjs-html']),
    [page],
  );

  usePageScripts(page);
  useSoftRedirect('#wrapper', [page]);

  if (!page) return <ErrorPage statusCode={404} />;

  let htmlContent: JSX.Element | undefined;

  if (page.gjs && page.gjs['gjs-html']) {
    // remove script from html - will be appended to body
    const pageHtml = page.gjs['gjs-html'].replace(PARSE_SCRIPT_REGEX, '');

    // Inject a script to tag all scripts as executed
    const html = `
      <style>${page.gjs['gjs-css']}</style>
      ${pageHtml}
    `;

    htmlContent = (
      <div
        id="wrapper"
        className={classes.wrapper}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }

  let content = (
    <div>
      {/* HEAD */}
      <PageHeader
        title={{ text: page.title }}
        description={{ text: page.description }}
        ogTitle={page.ogTitle ? { text: page.ogTitle } : undefined}
        ogDescription={
          page.ogDescription ? { text: page.ogDescription } : undefined
        }
        ogImage={page.ogImage}
      />
      {htmlContent}
    </div>
  );

  if (shouldAB || isHtmlAb)
    content = (
      <GoogleOptimizeWrapper preventDisplay>{content}</GoogleOptimizeWrapper>
    );

  return (
    <Root navOptions={{ backgroundTransparent: !htmlContent }}>{content}</Root>
  );
};

// this is NEEDED so this code is not available on FE
if (typeof window === 'undefined')
  PageComponent.getServerSideProps = async ({
    query,
    res,
    req,
    resolvedUrl,
  }) => {
    let path = query.path;

    // detect homepage
    const isHomepage = resolvedUrl.startsWith('/page') && !path;
    if (isHomepage) path = 'homepage';

    if (typeof query.pageId === 'string') {
      const token = getTokenFromRequest(req);
      const page = await pagePreviewGet.bind({ req })(query.pageId, token);

      if (page.redirect && res) {
        const redirectCode = isRedirectCode(page.redirectCode)
          ? page.redirectCode
          : 301;

        return {
          redirect: {
            statusCode: redirectCode,
            destination: page.redirect,
          },
        };
      }

      return { props: { pages: [page] } };
    } else if (path && typeof path === 'string') {
      const pages = await pagePublicGet.bind({ req })(path.replace(/^\//, ''));

      if (pages.length === 1) {
        const [page] = pages;

        if (page.redirect && res) {
          const redirectCode = isRedirectCode(page.redirectCode)
            ? page.redirectCode
            : 301;

          return {
            redirect: {
              statusCode: redirectCode,
              destination: page.redirect,
            },
          };
        }
      }

      return { props: { pages } };
    }

    return { notFound: true };
  };

export const Page = auth(PageComponent, ({ query }) => {
  if (query && query.pageId && typeof query.pageId === 'string') {
    return {
      userRequired: true,
      allowedUserRoles: ['ADMIN', 'EDITOR'],
    };
  }
  return {
    userRequired: false,
    shouldHandleRefreshToken: false,
    allowedUserRoles: [],
  };
});
